import React, { useEffect, useState } from "react";
import { assetUrl } from "../lib/api";
import { initials } from "../lib/domain";

/**
 * Avatar — image with graceful fallback to initials.
 *
 * Iter60.p10 — fixes the "MOH…" / "IDRUS…" garbage-inside-circle bug.
 * Previously the raw `<img>` had `alt={name}` and no `onError`
 * handler, so a broken URL (404 / missing file / expired CDN) let
 * the browser paint the alt text inside the image box. Users
 * reported seeing name fragments in circles for employees who had
 * uploaded photos that later went missing.
 *
 * Behaviour matrix:
 *   src empty / null              → initials
 *   src set, image loads          → image
 *   src set, image fails (onError) → initials (identical to no-src)
 *
 * The `alt` is now empty (decorative) so a failed image renders as
 * an empty box for the split second before React swaps to the
 * initials fallback. Real accessibility is provided by the enclosing
 * row (employee name is right next to the avatar in every usage).
 */
export default function Avatar({ src, name, size = 40 }) {
    const px = `${size}px`;
    const [errored, setErrored] = useState(false);

    // If the src prop changes (e.g. after Re-upload), give the new
    // URL a chance to load even if the previous URL had errored.
    useEffect(() => { setErrored(false); }, [src]);

    const showImage = !!src && !errored;

    if (showImage) {
        return (
            <img
                src={assetUrl(src)}
                alt=""
                onError={() => setErrored(true)}
                style={{
                    height: px,
                    width: px,
                    borderRadius: "9999px",
                    objectFit: "cover",
                }}
                data-testid="avatar-image"
            />
        );
    }
    return (
        <div
            style={{
                height: px,
                width: px,
                borderRadius: "9999px",
                fontSize: Math.round(size * 0.4),
            }}
            className="flex items-center justify-center text-white font-semibold asl-navy-bg"
            data-testid="avatar-initials"
            aria-label={name || "Employee avatar"}
        >
            {initials(name)}
        </div>
    );
}
